import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

const buttonVariants = cva(
  "items-center cursor-pointer justify-center gap-2 whitespace-nowrap rounded-3xl text-sm font-medium transition-all disabled:pointer-events-none disabled:cursor-not-allowed! disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ihs-blue focus-visible:ring-ihs-blue/20 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          'bg-ihs-blue text-primary-foreground shadow-xs hover:bg-ihs-blue/80',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-transparent border-ihs-blue text-ihs-blue shadow-xs hover:bg-ihs-blue/10 hover:text-ihs-blue dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-ihs-accent-blue text-ihs-blue',
        link: 'text-ihs-blue underline-offset-4 hover:underline',
      },
      display: {
        inline: 'inline-flex',
        block: 'flex',
        none: '',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-3xl gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-3xl px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      display: 'inline',
    },
  }
);

function Button({
  className,
  variant,
  display,
  size,
  asChild = false,
  children,
  isLoading,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, display, size, className }))}
      disabled={isLoading || props.disabled}
      {...props}
    >
      {isLoading && (
        <Loader2
          className={cn(
            'animate-spin size-4 text-primary-foreground',
            variant === 'outline' && 'text-ihs-blue'
          )}
        />
      )}
      {children}
    </Comp>
  );
}

export { Button, buttonVariants };
